import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserLocalStorageService } from 'app/permission/services/user.local.storage.service';
import { Constants } from 'app/shared/constants';
import { LogService } from 'app/shared/log/log.service';
import { Page } from 'app/shared/models/page';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators/map';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { EventCollisionService } from '../event-collision/event-collision.service';
import { MatchingProperty } from '../models/matching-property';
import { MatchingPropertyAssign } from '../models/matching-property-assign';
import { PropertyMatch } from '../models/property-match';
import { AuthenticationStore } from './../../permission/services/authentication.store';
import { MatchingPropertyHttpService } from './matching-property.http.service';
import { PagesizeType } from './pagination/pagesize-type';
import { EventCollisionInfo } from '../models/event-collision-info';
import { EventCollisionInfoVendor } from '../models/event-collision-info-vendor';
import { EventInfoForEventCollision } from '../models/event-info-for-event-collision';

@Injectable()
export class MatchingPropertyStore {

  private propertyPage: BehaviorSubject<Page<MatchingProperty>> = new BehaviorSubject(Page.empty<MatchingProperty>());

  constructor(
    private matchingPropertyHttpService: MatchingPropertyHttpService,
    private flashMessagesService: FlashMessagesService,
    private logService: LogService,
    private translateService: TranslateService,
    private authenticationStore: AuthenticationStore,
    private userLocalStorageService: UserLocalStorageService,
    private eventCollisionService: EventCollisionService
  ) {
    this.init();
  }

  private init(): void {
    if (this.authenticationStore.isSupportUser()) {
      this.getMatchingProperties(
        Constants.SEARCH_DEFAULT_PAGE,
        this.userLocalStorageService.getPagesize(PagesizeType.MATCHING_PROPERTY),
        Constants.DEFAULT_MATCHING_PROPERTY_FIELD_SORT_FIELD,
        null,
        Constants.SORT_DIRECTION_ASC
      ).take(1).subscribe();
    }
  }

  get properties(): Observable<Page<MatchingProperty>> {
    return this.propertyPage.asObservable();
  }

  get totalPropertyCount(): Observable<number> {
    return this.propertyPage.asObservable().map(page => page.totalElements);
  }

  getMatchingProperties(page: number, size: number, sortField: string, secondarySortField: string, sortDirection: string): Observable<any> {
    let sort = 'sort=' + sortField + ',' + sortDirection;
    if (secondarySortField) {
      sort = sort + '&sort=' + secondarySortField + ',' + sortDirection;
    }

    return this.matchingPropertyHttpService.getPropertyMatches(page, size, sort).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_MATCHING_PROPERTIES'));
      this.logService.error(`Error getting Matching Properties: ${error}`);
      return Observable.throw(error);
    }).map(propertyMatches => {
      this.propertyPage.next(propertyMatches);
      return propertyMatches;
    });
  }

  assignMatchingCandidate(property: MatchingPropertyAssign): Observable<any> {
    return this.matchingPropertyHttpService.assign(property)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.ASSIGN_MATCHING_PROPERTIES'));
        this.logService.error(`Error assigning Matching Properties: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.PROPERTY_REVIEW.CANDIDATE_ASSIGNED');
        return result;
      });
  }

  addProperty(newRec: PropertyMatch): Observable<MatchingProperty> {
    return this.matchingPropertyHttpService.add(newRec)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_PROPERTY', `Error adding property: ${error}`);
        return Observable.throw(error);
      })
      .map((res: MatchingProperty) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.ADD_PROPERTY_MATCHING');
        return res;
      });
  }

  /* getMatchingCollisionProperties(eventInfo: any): Observable<any> {
    const eventInfoList = {
      "event_info":[
        eventInfo
      ]
    }
    return this.matchingPropertyHttpService.getEventCollisionPropertyMatches(eventInfoList).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_MATCHING_PROPERTIES'));
      this.logService.error(`Error getting Matching Properties: ${error}`);
      return Observable.throw(error);
    }).map(propertyMatches => {
      this.propertyPage.next(propertyMatches);
      return propertyMatches;
    });
  } */

  getMatchingCollisionProperties(
    info: EventInfoForEventCollision,
    edited_event_id: any,
    user_id: string,
    ev_tmp_guid: string): Observable<any> {
    const eventCollisionInfo = this.buildEventInfoForEventCollision(info, edited_event_id, user_id, ev_tmp_guid);
    return this.eventCollisionService.getEventCollisionPropertyMatches(eventCollisionInfo)
    .catch(error => {
      return Observable.throw(error);
    })
    .map((res) => {
      return res;
    });
  }

  updateEventCollisionMatch(eventInfo: any): Observable<any> {
    return this.eventCollisionService.updateEventCollisionMatch(eventInfo)
    .pipe(map((response) => response));
  }

  cancelMatching(matchesToCancel: number[]) {
    return this.eventCollisionService.cancelEventCollisionMatch(matchesToCancel);
  }

  createNewMatching(eventMatchId: number) {
    return this.eventCollisionService.createNewEventCollisionMatch(eventMatchId);
  }

  unmatchEvent(unmatchInfo: any): Observable<any> {
    return this.eventCollisionService.unmatchEvent(unmatchInfo)
      .catch(error => {
        return Observable.throw(error);
      })
      .map((res) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.UNMATCH_EVENT');
        return res;
      });
  }

  private buildEventInfoForEventCollision(
    info: EventInfoForEventCollision,
    edited_event_id: any,
    user_id: string,
    ev_tmp_guid: string): EventCollisionInfo[] {

    const vendors = info.eventProperties.map(vendor => {
      return new EventCollisionInfoVendor({
        chain: vendor.chain,
        brand: vendor.brand,
        property_id: vendor.propertyId,
        property_name: vendor.name,
        property_address: `${vendor.line1} ${vendor.line2}`,
        property_zip: vendor.postalCode,
        property_city: vendor.city,
        property_state: vendor.state,
        property_country: vendor.countryCode,
        edited_vendor_code: vendor.chain + '|' + vendor.brand + '|' + vendor.propertyId,
      });
    });

    const event = info.event;
    const eventAgency = info.eventAgency;
    const eventClient = info.eventClient;
    const firstProperty = info.eventProperties[0];
    const isPropertyUser = this.authenticationStore.isPropertyUser();
    const contact = isPropertyUser ? firstProperty.contact : eventAgency.contact;
    const partyOfUser = isPropertyUser ? firstProperty : eventAgency;

    const eventCollisionInfo = new EventCollisionInfo({
      create_party: contact.partyType.toString() || '',
      event_name: event.name,
      start_date: event.startDate,
      end_date: event.endDate,
      first_name: contact.firstName || '',
      last_name: contact.lastName || '',
      email: contact.email || '',
      phone: contact.phone || '',
      fax: contact.fax || '',
      agency_id: eventAgency.agencyId ? eventAgency.agencyId : eventAgency.code,
      name: eventAgency.name,
      dba_name: eventAgency.dbaName,
      line1: eventAgency.line1,
      line2: eventAgency.line2,
      line3: eventAgency.line3,
      city: eventAgency.city,
      state: eventAgency.state,
      postal_code: eventAgency.postalCode,
      country_code: eventAgency.countryCode,
      event_reference_number: event.eventReferenceNumber,
      auto_generated_code: partyOfUser.autoGenerated ? 'Y' : 'N',
      manually_added: partyOfUser.manuallyAdded ? 'Y' : 'N',
      client_first_name: eventClient.contact.firstName ? eventClient.contact.firstName : '',
      client_last_name: eventClient.contact.lastName ? eventClient.contact.lastName : '',
      client_email: eventClient.contact.email ? eventClient.contact.email : '',
      client_phone: eventClient.contact.phone ? eventClient.contact.phone : '',
      client_fax: eventClient.contact.fax ? eventClient.contact.fax : '',
      client_name: eventClient.name ? eventClient.name : '',
      client_id: eventClient.clientReferenceNumber ? eventClient.clientReferenceNumber : '',
      client_line1: eventClient.line1 ? eventClient.line1 : '',
      client_city: eventClient.city ? eventClient.city : '',
      client_state: eventClient.state ? eventClient.state : '',
      client_zip: eventClient.postalCode ? eventClient.postalCode : '',
      action: 'ADD',
      user_id: user_id,
      ev_tmp_guid: ev_tmp_guid,
      edited_event_id: edited_event_id,
      edited_agency_id: eventAgency.agencyId ? eventAgency.agencyId : eventAgency.code,
      event_city: isPropertyUser ? firstProperty.city || '' : event.city || '',
      event_state: isPropertyUser ? firstProperty.state || '' : event.state || '',
      event_country_code: isPropertyUser ? firstProperty.countryCode || '' : event.countryCode || '',
      vendors: vendors,
    });
    return [eventCollisionInfo];
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/matching-property.store.ts